import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Modal, Popconfirm, PopconfirmProps } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Note } from '@/components/PdfReader/GeneratePdf/NotePdfDocument';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { notify } from '@/components/shared/basic/notify';
import { formateErrorAppwriteObject } from '@/components/shared/formateErrorObject';

import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { isFullScreenAtom } from '@/atoms/pdfStates/isFullScreen';
import { pageNumberAtom } from '@/atoms/pdfStates/pageNumber';
import { takeNoteModalAtom } from '@/atoms/pdfStates/takeNoteModalAtom';
import { deleteNote } from '@/services/Notes';

interface NoteContentProps {
  note: Note;
  onClose: () => void;
  navigateBack: () => void;
}

const NoteContent = ({ note, onClose, navigateBack }: NoteContentProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [, setPageNumber] = useRecoilState(pageNumberAtom);
  const [, setDrawerState] = useRecoilState(drawerPdfAtom);
  const [, setTakeNoteModal] = useRecoilState(takeNoteModalAtom);
  const isFullScreen = useRecoilValue(isFullScreenAtom);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const screens = useBreakpoint();

  const handleDeleteNote = async () => {
    if (!note.id) return;
    setDeleteLoading(true);

    try {
      await deleteNote(note.id);
      notify('Note deleted successfully', 'success');
      navigateBack && navigateBack();
    } catch (err: any) {
      notify(
        formateErrorAppwriteObject(err) || 'Failed to fetch note data',
        'error',
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirm: PopconfirmProps['onConfirm'] = () => {
    handleDeleteNote();
    setDrawerState({
      contentDrawer: false,
      notesDrawer: true,
    });
  };

  const handleModalOk = () => {
    confirm();
    setShowDeleteModal(false);
  };

  const handleModalCancel = () => {
    setShowDeleteModal(false);
  };
  return (
    <div className='flex flex-col items-center text-white hide-scrollbar'>
      <div className='flex justify-between w-full items-start  py-3.5 px-6 border-b border-[#282829] text-xs text-grayZl-600'>
        <div>Page no: {note.pageNumber}</div>
        <div>{note.date}</div>
      </div>

      <div className='flex justify-between items-center w-full px-6 py-4 border-b border-[#282829]'>
        <div className='flex gap-2'>
          <ButtonDefault
            size={screens.lg ? 2 : 1}
            variant={ButtonVariants.PRIMARY}
            onClick={() => {
              onClose && onClose();
              setPageNumber(note.pageNumber);
            }}
            className='w-min  whitespace-nowrap'
          >
            <div className='py-[1px] px-4'>View Page</div>
          </ButtonDefault>
        </div>
        <div className='flex gap-2'>
          <ButtonDefault
            size={screens.lg ? 2 : 1}
            onClick={() => {
              setTakeNoteModal({
                value: true,
                noteId: note.id,
                pageNumber: 0,
                selectedText: '',
                selectionRect: null,
              });
            }}
            variant={ButtonVariants.UNSTYLED}
            className='w-min whitespace-nowrap border border-grayZl-900 translate-y-0'
          >
            <div className='py-[1px] px-4'>Edit</div>
          </ButtonDefault>
          {isFullScreen ? (
            <>
              <ButtonDefault
                size={screens.lg ? 2 : 1}
                disabled={deleteLoading}
                variant={ButtonVariants.UNSTYLED}
                className='w-min whitespace-nowrap border border-grayZl-900 translate-y-0'
                onClick={() => {
                  setShowDeleteModal(true);
                }}
              >
                <div className='py-[1px] px-4'>Delete</div>
              </ButtonDefault>

              <Modal
                open={showDeleteModal}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={350}
                closable={false}
                className='customMaskModal rounded-2xl'
                footer={null}
                {...(isFullScreen && { getContainer: false })}
              >
                <div className='bg-[#121212] p-2 text-white border rounded-2xl border-[#3D3D3D]'>
                  <div className='w-full flex flex-col gap-y-4 justify-center py-2'>
                    <div className='flex justify-center gap-x-2 py-5'>
                      <div className='flex-col flex gap-y-4'>
                        <span className='text-center'>
                          <ExclamationCircleFilled
                            className='text-yellow-400'
                            style={{ fontSize: 30 }}
                          />
                        </span>
                        <span className='text-lg text-center'>
                          Sure want to delete this note?
                        </span>
                      </div>
                    </div>
                    <div className='flex justify-center px-5 md:px-10 gap-x-4'>
                      <ButtonDefault
                        size={1}
                        variant={ButtonVariants.UNSTYLED}
                        className='w-full p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d] disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                        onClick={handleModalCancel}
                      >
                        <span className='flex text-sm gap-x-2 px-2 whitespace-nowrap'>
                          No
                        </span>
                      </ButtonDefault>

                      <Button
                        disabled={deleteLoading}
                        loading={deleteLoading}
                        type='primary'
                        className='w-full'
                        onClick={handleModalOk}
                      >
                        Yes
                      </Button>
                    </div>
                  </div>
                </div>
              </Modal>
            </>
          ) : (
            <Popconfirm
              title='Delete the Note'
              description='Are you sure to delete this note?'
              onConfirm={confirm}
              okText='Yes'
              cancelText='No'
              placement='left'
              okButtonProps={{ loading: deleteLoading }}
            >
              <ButtonDefault
                size={screens.lg ? 2 : 1}
                disabled={deleteLoading}
                variant={ButtonVariants.UNSTYLED}
                className='w-min whitespace-nowrap border border-grayZl-900 translate-y-0'
              >
                <div className='py-[1px] px-4'>Delete</div>
              </ButtonDefault>
            </Popconfirm>
          )}
        </div>
      </div>

      <div className='flex justify-start items-center font-thin line-clamp-2 w-full bg-cartBgColor px-6 py-4 text-sm italic text-white/70'>
        “{note.excerpt}”
      </div>

      <div className='flex justify-start w-full items-start  px-6 py-4 gap-4 text-sm leading-relaxed'>
        {note.content}
      </div>
    </div>
  );
};

export default NoteContent;
